import React, { useState, useEffect } from 'react';
import { Box, Menu, MenuItem, IconButton, Typography, SxProps, Theme } from '@mui/material';
import { MoreVert, Close, Save } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch } from 'react-redux';
import { clearFormDraft, saveFormDraft, setSystemForm } from '../../redux/appConfigurator/action';
import { useSelector } from 'react-redux';
import { currentSystemForm } from '../../selectors/selectors';
import CloseIcon from '../CustomSVGIcons/CloseIcon';

function isEffectivelyEmpty(obj: Record<any, any>, field = 'forceUpdate') {
    if (typeof obj !== 'object' || obj === null) {
        throw new Error('Input must be a non-null object');
    }

    const keys = Object.keys(obj);

    // If no keys, it's empty
    if (keys.length === 0) return true;

    // If the only key is the given field, treat as empty
    if (keys.length === 1 && keys[0] === field) return true;

    // Otherwise, not empty
    return false;
}

export interface CloseConfirmationDropdownProps {
    onClose: () => void;
    hasUnsavedChanges?: boolean;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    formData: Record<string, any>;
}

const CloseConfirmationDropdown: React.FC<CloseConfirmationDropdownProps> = ({
    onClose,
    hasUnsavedChanges = true,
    disabled = false,
    sx,
    formData,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { themeStyle } = useThemeContext();
    const open = Boolean(anchorEl);
    const dispatch = useDispatch();
    const formType = useSelector(currentSystemForm);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCloseWithoutSaving = () => {
        dispatch(clearFormDraft({ formType: formType! }));
        handleClose();
        onClose();
    };

    const handleSaveDraftAndClose = () => {
        dispatch(saveFormDraft({ formData, formType: formType! }));
        dispatch(setSystemForm(undefined));
        handleClose();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (anchorEl && !anchorEl.contains(event.target as Node)) {
                setAnchorEl(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [anchorEl]);

    if (!formData || isEffectivelyEmpty(formData)) {
        return (
            <Box sx={sx}>
                <CloseIcon
                    onClick={handleCloseWithoutSaving}
                    style={{ cursor: 'pointer' }}
                    stroke={theme[themeStyle].tag_white}
                />
            </Box>
        );
    }

    return (
        <Box sx={sx}>
            <IconButton
                onClick={handleClick}
                disabled={disabled}
                sx={{
                    color: theme[themeStyle].tag_white,
                    '&:hover': {
                        backgroundColor: theme[themeStyle].gray_text,
                    },
                }}
                aria-label="close options">
                <MoreVert sx={{ color: theme[themeStyle].tag_white }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].gray_text,
                        color: theme[themeStyle].tag_white,
                        minWidth: 200,
                        '& .MuiMenuItem-root': {
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: '14px',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <MenuItem onClick={handleCloseWithoutSaving}>
                    <Close sx={{ mr: 1, fontSize: '18px', color: theme[themeStyle].tag_white }} />
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: '14px' }}>Close</Typography>
                </MenuItem>
                {hasUnsavedChanges && (
                    <MenuItem onClick={handleSaveDraftAndClose}>
                        <Save sx={{ mr: 1, fontSize: '18px' }} />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: '14px' }}>
                            Save Draft and Close
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export { CloseConfirmationDropdown };
